import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useSFX } from 'js/hooks/useSFX';

// import actions
import { toggleModalOverlay } from 'js/store/app/App.actions';

// import hooks
import { useRouteInfo } from 'js/hooks/useRouteInfo';

// import selectors
import { getCollectionById, isUserCollection } from 'js/store/collections/Collections.selectors';
import { getTranslationById, getSiteMetaData } from 'js/store/app/App.selectors';

// import components
import GridCardList from 'js/containers/grid/Grid.card-list';
import HeaderFixed from 'js/components/header/Header.fixed';
import ButtonWithIcon from 'js/components/button/Button.with-icon';
import GenericMetadata from 'js/components/generic/Generic.metadata';
// import actions
import { toggleFavoriteCuratedCollection } from 'js/store/user/User.actions.js';
import { trackEvent } from '../../../utils/tracking';

// import icons
import IconHeart from 'js/components/icon/Icon.heart';
import { getSkillTagTranslation } from '../../../store/app/App.selectors';

const CollectionViewCollection = ({ collectionData = false }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { playSFX } = useSFX();

  const siteMetaData = useSelector((state) => getSiteMetaData(state, '/collections/{id}'));

  const buttonTitles = {
    save: useSelector((state) => getTranslationById(state, 'collections_page_save_button_text', 'Save')),
    share: useSelector((state) => getTranslationById(state, 'collections_page_share_button_text', 'Share')),
    delete: useSelector((state) => getTranslationById(state, 'collections_page_delete_button_text', 'Delete')),
    edit: useSelector((state) => getTranslationById(state, 'collections_page_edit_button_text', 'Edit')),
    activity: useSelector((state) => getTranslationById(state, 'collections_activity', 'Activity')),
    activities: useSelector((state) => getTranslationById(state, 'collections_activities', 'Activities')),
  };

  const { state: { displayOtherButtons = false } = {}, params: { collectionId } = {} } = useRouteInfo();

  let collection = useSelector((state) => getCollectionById(state, collectionId));
  let userCollection = useSelector((state) => isUserCollection(state, collectionId));

  if (collectionData) {
    collection = collectionData;
  }

  const { cards, name, id, curated = false } = collection || {};

  const locasedTagName = useSelector((state) => getSkillTagTranslation(state, id, false));
  const localisedName = locasedTagName || useSelector((state) => getTranslationById(state, name, name));

  React.useEffect(() => {
    if (userCollection) {
      trackEvent('game_collection_select_own', { collection_id: id });
    } else {
      trackEvent('game_collection_select', { collection_id: id });
    }
  }, []);

  const triggerCollectionModal = (modalState) => {
    dispatch(
      toggleModalOverlay({
        type: 'COLLECTION',
        collectionID: id,
        modalState,
        collectionData: collectionData || false,
      })
    );
  };

  const metaTitle = siteMetaData?.title?.replace('{name}', localisedName) || localisedName;
  const metaDescription = siteMetaData?.description?.replace('{name}', localisedName) || '';
  const metaKeywords = siteMetaData?.keywords?.replace('{name}', localisedName) || '';

  const favorited = useSelector((state) => {
    return state?.user?.favoriteCuratedCollections?.includes(id) || false;
  });

  const addToFavorites = (e) => {
    playSFX('hearting_a_card_feedback');
    e.stopPropagation();
    e.preventDefault();
    trackEvent(favorited ? 'game_collection_unfavorite' : 'game_collection_favorite', {
      collection_id: id,
    });
    dispatch(toggleFavoriteCuratedCollection(id));
  };

  return (
    <>
      <GenericMetadata title={metaTitle} description={metaDescription} keywords={metaKeywords} />
      <HeaderFixed
        title={localisedName}
        buttonBackAction={() => navigate(-1)}
        additionalText={`${cards?.length || 0} ${
          cards?.length === 1 ? buttonTitles.activity : buttonTitles.activities
        }`}
        backgroundColor={displayOtherButtons ? '#F7EBB1' : '#BBDCD5'}
      />
      <div style={{ marginTop: '25px', marginBottom: '20px', display: 'flex', gap: '10px' }}>
        {collectionData ? (
          <ButtonWithIcon title={buttonTitles.save} action={() => triggerCollectionModal('save')} iconName={'save'} />
        ) : (
          <ButtonWithIcon
            title={buttonTitles.share}
            action={() => {
              trackEvent(userCollection ? 'game_collection_share_own' : 'game_collection_share', { collection_id: id });
              triggerCollectionModal('share');
            }}
            iconName={'share'}
          />
        )}
        {!displayOtherButtons && (
          <button className="button--icon" onClick={addToFavorites}>
            <span className="button__icon">
              <IconHeart active={favorited} stroke={'black'} fill={'black'} shadowOnActive={false} />
            </span>
          </button>
        )}
        {displayOtherButtons && (
          <>
            <ButtonWithIcon
              title={buttonTitles.delete}
              action={() => {
                trackEvent(userCollection ? 'game_collection_delete_own' : 'game_collection_delete', {
                  collection_id: id,
                });
                triggerCollectionModal('delete');
              }}
              iconName={'delete'}
            />
            <ButtonWithIcon title={buttonTitles.edit} action={() => triggerCollectionModal('edit')} iconName={'edit'} />
          </>
        )}
      </div>
      <GridCardList cards={cards} />
    </>
  );
};

export default CollectionViewCollection;
